import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '@awj/api-client';
import { formatDateTime, formatMoney, formatNumber } from '@awj/i18n';
import { Button, Dialog, Skeleton, useToast } from '@awj/ui';

import { ExportMenu } from '~/components/ExportMenu.tsx';
import { FieldHead } from '~/components/InfoHint.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { orderItemColumns } from '~/lib/export-columns.ts';
import { useMarkRefunded, useOrder, useOrderTransitions, useTransitionOrder } from '~/lib/order-queries.ts';
import { orderNote } from '~/lib/order-notes.ts';

/**
 * One order, and the two things staff do with it: read it, and move it along.
 *
 * The actions offered are the ones the server says are legal — asked for rather than worked out
 * here, so a button never appears that the order will refuse. Cancelling is set apart from the
 * rest because it is the one that cannot be walked back.
 */
export function OrderDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { number = '' } = useParams();

  const order = useOrder(number);
  const transitions = useOrderTransitions(number);
  const transition = useTransitionOrder(number);
  const markRefunded = useMarkRefunded(number);

  const [pending, setPending] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [courier, setCourier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [recordingRefund, setRecordingRefund] = useState(false);
  const [refundReference, setRefundReference] = useState('');

  async function recordRefund() {
    try {
      await markRefunded.mutateAsync(refundReference);
      toast.show({ title: t('orders.refundRecorded'), tone: 'success' });
      setRecordingRefund(false);
    } catch (error) {
      toast.show({
        title: t('orders.couldNotRecordRefund'),
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
    }
  }

  async function apply() {
    if (!pending) {
      return;
    }

    try {
      await transition.mutateAsync({
        status: pending,
        note: note.trim() || null,
        courier: pending === 'Shipped' ? courier.trim() || null : null,
        trackingNumber: pending === 'Shipped' ? trackingNumber.trim() || null : null,
      });
      toast.show({ title: t('orders.moved', { status: t(`orderStatus.${pending}` as 'orderStatus.Paid') }), tone: 'success' });
      setPending(null);
      setNote('');
    } catch (error) {
      toast.show({
        title: t('orders.couldNotMove'),
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
    }
  }

  if (order.isPending) {
    return (
      <>
        <PageTitle title={t('orders.order')} />
        <Skeleton height="420px" />
      </>
    );
  }

  if (order.isError) {
    return (
      <>
        <PageTitle title={t('orders.order')} />
        <div className="empty-panel">
          <p>{t('orders.notFound')}</p>
          <Button variant="secondary" onClick={() => navigate('/orders')}>
            {t('nav.orders')}
          </Button>
        </div>
      </>
    );
  }

  const data = order.data;
  const next = transitions.data ?? [];
  const refundOutstanding = data.refundDueAt != null && data.refundedAt == null;

  return (
    <>
      <PageTitle
        title={data.number}
        back="/orders"
        actions={
          <>
            {next
              // Cancel sits last and reads as the exception it is.
              .filter((status) => status !== 'Cancelled')
              .map((status, index) => (
                <Button
                  key={status}
                  type="button"
                  /*
                   * The next step forward is the primary action, whatever it happens to be.
                   * Marking only "Delivered" made the button a person actually presses forty
                   * times a day — "start preparing" — the quiet one on the row.
                   */
                  variant={index === 0 ? 'primary' : 'secondary'}
                  onClick={() => {
                    setPending(status);
                    setNote('');
                  }}
                >
                  {t(`orderAction.${status}` as 'orderAction.Paid')}
                </Button>
              ))}
            {next.includes('Cancelled') ? (
              /*
               * Quiet until it is chosen. A filled red button beside the ordinary next step makes
               * cancelling look like the thing to do; it is confirmed in a dialog anyway, and the
               * dialog is where it turns red.
               */
              <Button
                type="button"
                variant="ghost"
                className="awj-button--quietDanger"
                onClick={() => {
                  setPending('Cancelled');
                  setNote('');
                }}
              >
                {t('orderAction.Cancelled')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="order-head">
        <span className={`status status--${data.status.toLowerCase()}`}>
          {t(`orderStatus.${data.status}` as 'orderStatus.Paid')}
        </span>
        <span className="order-head__meta">
          {t('orders.placedAt', { at: formatDateTime(locale, data.placedAt) })}
        </span>
        {data.paidAt ? (
          <span className="order-head__meta">
            {t('orders.paidAt', { at: formatDateTime(locale, data.paidAt) })}
          </span>
        ) : null}
        {data.cancelledAt ? (
          <span className="order-head__meta">
            {t('orders.cancelledAt', { at: formatDateTime(locale, data.cancelledAt) })}
          </span>
        ) : null}
        {/*
          Only once the moves have actually arrived. Read off `transitions.data ?? []`, a request
          still in flight — or one that failed — looked exactly like "no moves left", and a paid
          order waiting to be prepared was announced as finished.
        */}
        {refundOutstanding ? (
          <span className="status status--draft">{t('orders.refundDue')}</span>
        ) : transitions.isSuccess && next.length === 0 ? (
          <span className="order-head__meta">{t('orders.finished')}</span>
        ) : null}
        {transitions.isError ? (
          <span className="order-head__meta" role="alert">
            {t('orders.nextStepsFailed')}{' '}
            <Button type="button" variant="ghost" size="sm" onClick={() => transitions.refetch()}>
              {t('common.retry')}
            </Button>
          </span>
        ) : null}
      </div>

      <div className="editor">
        <div className="editor__main">
          <section className="card card--flush" aria-labelledby="items-title">
            <div className="card__head order-card__title">
              <h2 className="section-label" id="items-title">
                {t('orders.items')}
              </h2>
              <ExportMenu
                file={`order-${data.number}`}
                sheet={t('orders.items')}
                title={t('export.itemsIn', { number: data.number })}
                columns={orderItemColumns(t, locale)}
                rows={() => data.items}
                variant="ghost"
              />
            </div>

            <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('a11y.scrollableTable')}>
              <table className="data-table__table">
                <thead>
                  <tr>
                    <th className="data-table__th" scope="col">{t('orders.item')}</th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('orders.unitPrice')}
                    </th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('orders.quantity')}
                    </th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('orders.lineTotal')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, index) => (
                    <tr key={`${item.sku}-${index}`}>
                      <td className="data-table__td">
                        <bdi>{item.name}</bdi>
                        {item.variantLabel ? (
                          <span className="data-table__sub">
                            <bdi>{item.variantLabel}</bdi>
                          </span>
                        ) : null}
                        <span className="data-table__sub mono">{item.sku}</span>
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatMoney(locale, item.unitPrice)}</bdi>
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatNumber(locale, item.quantity)}</bdi>
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatMoney(locale, item.lineTotal)}</bdi>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/*
              The totals repeat the arithmetic the customer saw at checkout, in the same order, so
              a query about "why does this say 245" can be answered from this screen alone.
            */}
            <dl className="totals">
              <div>
                <dt>{t('orders.subtotal')}</dt>
                <dd>{formatMoney(locale, data.subtotal)}</dd>
              </div>
              {data.discountAmount > 0 ? (
                <div>
                  <dt>
                    {t('orders.discount')}
                    {data.couponCode ? <span className="totals__note mono">{data.couponCode}</span> : null}
                  </dt>
                  <dd>−{formatMoney(locale, data.discountAmount)}</dd>
                </div>
              ) : null}
              <div>
                <dt>{t('orders.shipping')}</dt>
                <dd>
                  {data.shippingFee > 0 ? formatMoney(locale, data.shippingFee) : t('orders.freeShipping')}
                </dd>
              </div>
              <div className="totals__grand">
                <dt>{t('orders.total')}</dt>
                <dd>{formatMoney(locale, data.grandTotal)}</dd>
              </div>
              <div className="totals__vat">
                <dt>{t('orders.vatIncluded')}</dt>
                <dd>{formatMoney(locale, data.vatAmount)}</dd>
              </div>
            </dl>
          </section>

          <section className="card" aria-labelledby="timeline-title">
            <h2 className="section-label" id="timeline-title">
              {t('orders.timeline')}
            </h2>

            {/*
              Newest first: the question this answers is almost always "what happened last", and
              a long order's most recent entry should not be at the bottom of a scroll.
            */}
            <ol className="timeline">
              {[...data.timeline].reverse().map((entry, index) => (
                <li className="timeline__entry" key={`${entry.occurredAt}-${index}`}>
                  <span className={`timeline__dot timeline__dot--${entry.status.toLowerCase()}`} aria-hidden="true" />
                  <div>
                    <p className="timeline__status">
                      {t(`orderStatus.${entry.status}` as 'orderStatus.Paid')}
                    </p>
                    <p className="timeline__when">
                      <bdi>{formatDateTime(locale, entry.occurredAt)}</bdi>
                      {entry.actorEmail ? (
                        <>
                          {' · '}
                          <bdi>{entry.actorEmail}</bdi>
                        </>
                      ) : (
                        // No actor means the shop itself did it — a payment landing, or the
                        // unpaid-order sweep. Said plainly rather than left blank.
                        <>
                          {' · '}
                          {t('orders.bySystem')}
                        </>
                      )}
                    </p>
                    {entry.note ? (
                      <p className="timeline__note">
                        <bdi>{orderNote(entry.note, t)}</bdi>
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <aside className="editor__side">
          <section className="card" aria-labelledby="customer-title">
            <h2 className="section-label" id="customer-title">
              {t('orders.customer')}
            </h2>
            <p className="detail-line detail-line--strong">
              <bdi>{data.shipToName}</bdi>
            </p>
            <p className="detail-line">
              {/* Both are one tap away on a phone, which is how staff actually chase an order. */}
              <a href={`mailto:${data.email}`}>
                <bdi>{data.email}</bdi>
              </a>
            </p>
            <p className="detail-line">
              <a href={`tel:${data.shipToPhone}`} dir="ltr">
                {data.shipToPhone}
              </a>
            </p>
          </section>

          <section className="card" aria-labelledby="shipping-title">
            <h2 className="section-label" id="shipping-title">
              {t('orders.shipTo')}
            </h2>
            <p className="detail-line">
              <bdi>{data.shipToLine1}</bdi>
            </p>
            {data.shipToLine2 ? (
              <p className="detail-line">
                <bdi>{data.shipToLine2}</bdi>
              </p>
            ) : null}
            <p className="detail-line">
              <bdi>{data.shipToCity}</bdi>
            </p>
            {/*
              Half the UAE's cities share a name with their emirate. "Dubai / Dubai" on two lines
              reads as a mistake in the address rather than a fact about the country.
            */}
            {data.shipToEmirate !== data.shipToCity ? (
              <p className="detail-line">
                <bdi>{data.shipToEmirate}</bdi>
              </p>
            ) : null}
            {data.deliveryNotes ? (
              <p className="detail-line detail-line--quiet">
                <bdi>{data.deliveryNotes}</bdi>
              </p>
            ) : null}
            {data.courier ? (
              <p className="detail-line detail-line--quiet">
                <bdi>{data.courier}</bdi>
              </p>
            ) : null}
            {data.trackingNumber ? (
              <p className="detail-line mono" dir="ltr">
                {data.trackingNumber}
              </p>
            ) : null}
          </section>

          <section className="card" aria-labelledby="payment-title">
            <h2 className="section-label" id="payment-title">
              {t('orders.payment')}
            </h2>
            <p className="detail-line">
              {data.paidAt ? t('orders.paid') : t('orders.notPaid')}
            </p>
            {data.paymentReference ? (
              <p className="detail-line mono">{data.paymentReference}</p>
            ) : (
              <p className="field__hint">{t('orders.noReference')}</p>
            )}

            {/*
              Refunds are made by hand, at the bank or in the gateway dashboard. This is where staff
              see one is owed and record that it was paid, so a cancelled order is not "finished"
              while the customer is still out of pocket.
            */}
            {refundOutstanding ? (
              <div className="refund-note">
                <p className="detail-line detail-line--strong">
                  {t('orders.refundDueAmount', { amount: formatMoney(locale, data.grandTotal) })}
                </p>
                <p className="field__hint">{t('orders.refundHow')}</p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setRefundReference('');
                    setRecordingRefund(true);
                  }}
                >
                  {t('orders.recordRefund')}
                </Button>
              </div>
            ) : null}
            {data.refundedAt ? (
              <div className="refund-note">
                <p className="detail-line">
                  {t('orders.refundedAt', { at: formatDateTime(locale, data.refundedAt) })}
                </p>
                {data.refundReference ? <p className="detail-line mono">{data.refundReference}</p> : null}
              </div>
            ) : null}
          </section>
        </aside>
      </div>

      <Dialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending ? t('orders.confirmMove', { status: t(`orderStatus.${pending}` as 'orderStatus.Paid') }) : ''}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setPending(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant={pending === 'Cancelled' ? 'danger' : 'primary'}
              loading={transition.isPending}
              onClick={apply}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <div className="transition-form">
          {pending === 'Cancelled' && data.paidAt ? (
            <p className="dialog-lede">
              {t('orders.cancelPaidHint', { amount: formatMoney(locale, data.grandTotal) })}
            </p>
          ) : null}
          {/* Who is carrying it and the number to follow it by, asked only when it leaves. */}
          {pending === 'Shipped' ? (
            <div className="part-fields__pair">
              <label className="field">
                <span>{t('orders.courier')}</span>
                <input
                  className="awj-input"
                  maxLength={80}
                  value={courier}
                  onChange={(event) => setCourier(event.target.value)}
                />
              </label>
              <label className="field">
                <span>{t('orders.trackingNumber')}</span>
                <input
                  className="awj-input"
                  dir="ltr"
                  maxLength={80}
                  value={trackingNumber}
                  onChange={(event) => setTrackingNumber(event.target.value)}
                />
              </label>
            </div>
          ) : null}
          <label className="field">
            <span>{t('orders.note')}</span>
            <textarea
              className="awj-textarea"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            {/*
              Said before it is typed, not after: the note lands on the customer's own order page
              and in the email they get, and that is not something to discover afterwards.
            */}
            <span className="field__hint">
              {/*
                Only promise an email that will go. "Being prepared" sends none, and an order with
                no email address on it (an older guest order) can only be followed by its link.
              */}
              {pending === 'Processing'
                ? t('orders.noteHintNoEmail')
                : data.email
                  ? t('orders.noteHint')
                  : t('orders.noteHintNoAddress')}
            </span>
          </label>
        </div>
      </Dialog>

      <Dialog
        open={recordingRefund}
        onClose={() => setRecordingRefund(false)}
        title={t('orders.recordRefundTitle', { amount: formatMoney(locale, data.grandTotal) })}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setRecordingRefund(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" loading={markRefunded.isPending} onClick={recordRefund}>
              {t('orders.markRefunded')}
            </Button>
          </>
        }
      >
        <div className="transition-form">
          <label className="field">
            <span>{t('orders.refundReference')}</span>
            <input
              className="awj-input"
              maxLength={128}
              value={refundReference}
              onChange={(event) => setRefundReference(event.target.value)}
            />
            <span className="field__hint">{t('orders.refundReferenceHint')}</span>
          </label>
        </div>
      </Dialog>
    </>
  );
}
